import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  addSupportTicketComment,
  createSupportTicket,
  getSupportProfile,
  getSupportTicket,
  reviewSupportTicket,
  searchSupportTickets,
  SUPPORT_ENVIRONMENT_LABELS,
  SUPPORT_STATUS_LABELS,
  supportAttachmentUrl,
  updateSupportTicketStatus,
} from "./supportService";
import SupportNarrativeEditor, {
  SupportNarrative,
} from "./SupportNarrativeEditor";

const DEFAULT_ALLOWED_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".xlsm",
  ".xlsb",
  ".csv",
  ".txt",
  ".rtf",
  ".ppt",
  ".pptx",
];

const GRIER_SUPPORT_USER_ID = "51db55c0-2041-7076-e06f-6c5eae86e092";

function shouldDefaultToSharedTickets(profile) {
  return Boolean(
    (profile?.can_collaborate && !profile?.can_manage) ||
      (profile?.can_manage && profile?.user_id === GRIER_SUPPORT_USER_ID),
  );
}

function formatTimestamp(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  }).format(parsed);
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function SupportSectionLoading({ label, className = "" }) {
  return (
    <div
      className={`support-section-loading${className ? ` ${className}` : ""}`}
      role="status"
      aria-live="polite"
    >
      <span
        className="support-loading-spinner spinner-border spinner-border-sm text-primary"
        aria-hidden="true"
      />
      <strong>{label}</strong>
    </div>
  );
}

function supportNarrativePreview(value) {
  return String(value || "")
    .replace(/\[\[support-image:([^\]]+)\]\]/g, (_token, encodedName) => {
      try {
        return `[Screenshot: ${decodeURIComponent(encodedName)}]`;
      } catch {
        return "[Screenshot]";
      }
    })
    .replace(/\s+/g, " ")
    .trim();
}

function preventImplicitTicketSubmit(event) {
  if (event.key !== "Enter" || event.isComposing) return;
  const tagName = String(event.target?.tagName || "").toLowerCase();
  if (tagName === "textarea" || tagName === "button") return;
  event.preventDefault();
}

function mergeSelectedFiles(currentFiles, fileList) {
  const merged = [...currentFiles];
  for (const file of Array.from(fileList || [])) {
    const alreadySelected = merged.some(
      (current) =>
        current.name === file.name &&
        current.size === file.size &&
        current.lastModified === file.lastModified,
    );
    if (!alreadySelected) merged.push(file);
  }
  return merged;
}

function StagedFileList({ files, destination, onRemove }) {
  if (!files.length) return null;
  return (
    <div
      className="support-staged-files"
      aria-label={`Files staged for this ${destination}`}
    >
      <div className="support-staged-heading">
        <strong>
          {files.length} file{files.length === 1 ? "" : "s"} staged for this{" "}
          {destination}
        </strong>
        <span>
          Files are attached when you{" "}
          {destination === "ticket" ? "submit the request" : "add the comment"}.
        </span>
      </div>
      <div className="support-staged-list">
        {files.map((file, index) => (
          <div
            className="support-staged-file"
            key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
          >
            <span className="support-staged-check" aria-hidden="true">
              ✓
            </span>
            <span className="support-staged-file-name">
              <strong>{file.name}</strong>
              <small>{formatBytes(file.size)}</small>
            </span>
            <span className="support-staged-status">
              Staged for {destination}
            </span>
            <button
              className="support-staged-remove"
              type="button"
              aria-label={`Remove ${file.name}`}
              onClick={() => onRemove(file)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function activityHeading(activity) {
  switch (activity?.activity_type) {
    case "created":
      return "Ticket submitted";
    case "comment":
      return "Comment added";
    case "attachment_added":
      return "Attachment added";
    case "status_change":
      return "Status updated";
    case "requester_review":
      return "Requester review";
    default:
      return "Activity";
  }
}

function activityActorLabel(activity, ticket) {
  if (activity?.actor_role === "support_agent") return "Vicus Support";
  const requesterValues = [
    ticket?.requester_user_id,
    ticket?.requester_login,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());
  const actorValues = [activity?.actor_user_id, activity?.actor_login]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase());
  return actorValues.some((value) => requesterValues.includes(value))
    ? "Requester"
    : "Collaborator";
}

function activityTechnicalNote(activity) {
  const context = activity?.metadata?.technical_context;
  if (!context || typeof context !== "object") return "";
  const environment =
    SUPPORT_ENVIRONMENT_LABELS[context.reported_environment] ||
    context.reported_environment ||
    "Unknown site";
  return [
    environment,
    `Frontend ${context.frontend_version || "Not captured"}`,
    `Proposal API ${context.proposal_api_version || "Not captured"}`,
    `Support API ${context.support_api_version || "Not captured"}`,
  ].join(" · ");
}

function statusClass(status) {
  return `support-status support-status-${String(
    status || "submitted",
  ).replaceAll("_", "-")}`;
}

function ticketCardStatusClass(status) {
  return `support-ticket-card-${String(status || "submitted").replaceAll(
    "_",
    "-",
  )}`;
}

function nextSupportStatus(currentStatus) {
  if (currentStatus === "in_process") {
    return "scheduled_for_release";
  }
  if (currentStatus === "scheduled_for_release") {
    return "waiting_for_user_review";
  }
  return "in_process";
}

function ticketStatusTransition(activity) {
  if (!activity?.to_status_label) return "";
  if (!activity.from_status_label) return activity.to_status_label;
  return `${activity.from_status_label} → ${activity.to_status_label}`;
}

export default function SupportPage() {
  const [profileState, setProfileState] = useState({
    status: "loading",
    data: null,
    errorMessage: "",
  });
  const [ticketsState, setTicketsState] = useState({
    status: "loading",
    tickets: [],
    totalCount: 0,
    requesters: [],
    statusCounts: {},
    errorMessage: "",
  });
  const [detailState, setDetailState] = useState({
    status: "idle",
    data: null,
    errorMessage: "",
  });
  const [loadingTicketId, setLoadingTicketId] = useState("");
  const [scope, setScope] = useState("mine");
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [selectedRequesterIds, setSelectedRequesterIds] = useState([]);
  const [reportedEnvironment, setReportedEnvironment] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [createForm, setCreateForm] = useState({
    subject: "",
    description: "",
    files: [],
    inlineFileNames: [],
  });
  const [commentForm, setCommentForm] = useState({
    message: "",
    files: [],
    inlineFileNames: [],
  });
  const [statusForm, setStatusForm] = useState({
    status: "in_process",
    message: "",
  });
  const [reviewMessage, setReviewMessage] = useState("");
  const [createFileKey, setCreateFileKey] = useState(0);
  const [commentFileKey, setCommentFileKey] = useState(0);
  const [createEditorKey, setCreateEditorKey] = useState(0);
  const [commentEditorKey, setCommentEditorKey] = useState(0);
  const [createDropActive, setCreateDropActive] = useState(false);
  const [mutationState, setMutationState] = useState({
    status: "idle",
    message: "",
    errorMessage: "",
  });

  const profile = profileState.data;
  const canViewShared = Boolean(
    profile?.can_manage || profile?.can_collaborate,
  );
  const selectedTicket = detailState.data?.ticket || null;
  const allowedExtensions = profile?.allowed_extensions?.length
    ? profile.allowed_extensions
    : DEFAULT_ALLOWED_EXTENSIONS;
  const acceptValue = allowedExtensions.join(",");

  const loadProfile = useCallback(async () => {
    try {
      const result = await getSupportProfile();
      setProfileState({
        status: "ready",
        data: result,
        errorMessage: "",
      });
      if (shouldDefaultToSharedTickets(result)) {
        setScope("all");
      }
    } catch (error) {
      setProfileState({
        status: "error",
        data: null,
        errorMessage: error.message || "Support access could not be loaded.",
      });
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setTicketsState((current) => ({
      ...current,
      status: "loading",
      errorMessage: "",
    }));
    try {
      const result = await searchSupportTickets({
        scope,
        include_completed: includeCompleted,
        requester_user_ids: scope === "all" ? selectedRequesterIds : [],
        reported_environments:
          scope === "all" && reportedEnvironment
            ? [reportedEnvironment]
            : [],
        query: appliedSearch,
        limit: 200,
      });
      const tickets = Array.isArray(result?.tickets) ? result.tickets : [];
      const reportedTotal = Number(result?.total_count);
      setTicketsState({
        status: "ready",
        tickets,
        totalCount: Number.isFinite(reportedTotal)
          ? reportedTotal
          : tickets.length,
        requesters: Array.isArray(result?.requesters) ? result.requesters : [],
        statusCounts:
          result?.status_counts && typeof result.status_counts === "object"
            ? result.status_counts
            : {},
        errorMessage: "",
      });
    } catch (error) {
      setTicketsState({
        status: "error",
        tickets: [],
        totalCount: 0,
        requesters: [],
        statusCounts: {},
        errorMessage: error.message || "Support tickets could not be loaded.",
      });
    }
  }, [
    appliedSearch,
    includeCompleted,
    reportedEnvironment,
    scope,
    selectedRequesterIds,
  ]);

  const openTicket = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setLoadingTicketId(ticketId);
    setDetailState((current) => ({
      status: "loading",
      data: current.data,
      errorMessage: "",
    }));
    setMutationState({
      status: "idle",
      message: "",
      errorMessage: "",
    });
    try {
      const result = await getSupportTicket(ticketId);
      setDetailState({
        status: "ready",
        data: result,
        errorMessage: "",
      });
      setLoadingTicketId("");
      const currentStatus = result?.ticket?.status || "submitted";
      setStatusForm({
        status: nextSupportStatus(currentStatus),
        message: "",
      });
      setReviewMessage("");
      setCommentForm({
        message: "",
        files: [],
        inlineFileNames: [],
      });
      setCommentEditorKey((value) => value + 1);
    } catch (error) {
      setDetailState({
        status: "error",
        data: null,
        errorMessage:
          error.message || "The support ticket could not be loaded.",
      });
      setLoadingTicketId("");
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (profileState.status === "ready" && profile?.has_access) {
      void loadTickets();
    }
  }, [loadTickets, profile?.has_access, profileState.status]);

  useEffect(() => {
    if (!canViewShared && scope === "all") {
      setScope("mine");
      setSelectedRequesterIds([]);
      setReportedEnvironment("");
    }
  }, [canViewShared, scope]);

  const activeTicketId = loadingTicketId || selectedTicket?.ticket_id || "";
  const ticketList = ticketsState.tickets;
  const requesterOptions = ticketsState.requesters;
  const statusSummary = useMemo(
    () =>
      Object.entries(profile?.status_labels || SUPPORT_STATUS_LABELS).map(
        ([status, label]) => ({
          status,
          label,
          count: Number(ticketsState.statusCounts?.[status] || 0),
        }),
      ),
    [profile?.status_labels, ticketsState.statusCounts],
  );
  const activity = detailState.data?.activity || [];
  const attachments = detailState.data?.attachments || [];
  const availableStatusOptions = useMemo(
    () =>
      (profile?.agent_statuses || []).filter(
        (option) => option.value !== selectedTicket?.status,
      ),
    [profile?.agent_statuses, selectedTicket?.status],
  );
  const statusNoteRequired = statusForm.status === "waiting_for_user_review";

  async function refreshAfterMutation(result, successMessage) {
    setDetailState({
      status: "ready",
      data: result,
      errorMessage: "",
    });
    setMutationState({
      status: "success",
      message: successMessage,
      errorMessage: "",
    });
    await loadTickets();
  }

  function resetCreateDraft() {
    setCreateForm({
      subject: "",
      description: "",
      files: [],
      inlineFileNames: [],
    });
    setCreateDropActive(false);
    setCreateFileKey((value) => value + 1);
    setCreateEditorKey((value) => value + 1);
  }

  function cancelCreateDraft() {
    resetCreateDraft();
    setMutationState({
      status: "idle",
      message: "",
      errorMessage: "",
    });
  }

  async function handleCreate(event) {
    event.preventDefault();
    if (!createForm.description.trim()) {
      setMutationState({
        status: "error",
        message: "",
        errorMessage:
          "Add a description or paste a screenshot before submitting.",
      });
      return;
    }
    setMutationState({
      status: "saving",
      message: "",
      errorMessage: "",
    });
    try {
      const result = await createSupportTicket(createForm);
      resetCreateDraft();
      await refreshAfterMutation(result, "Support ticket submitted.");
    } catch (error) {
      setMutationState({
        status: "error",
        message: "",
        errorMessage:
          error.message || "The support ticket could not be submitted.",
      });
    }
  }

  async function handleComment(event) {
    event.preventDefault();
    if (!selectedTicket) return;
    setMutationState({
      status: "saving",
      message: "",
      errorMessage: "",
    });
    try {
      const result = await addSupportTicketComment({
        ticketId: selectedTicket.ticket_id,
        message: commentForm.message,
        expectedVersion: selectedTicket.version,
        files: commentForm.files,
      });
      setCommentForm({
        message: "",
        files: [],
        inlineFileNames: [],
      });
      setCommentFileKey((value) => value + 1);
      setCommentEditorKey((value) => value + 1);
      await refreshAfterMutation(result, "Comment added.");
    } catch (error) {
      setMutationState({
        status: "error",
        message: "",
        errorMessage: error.message || "The comment could not be added.",
      });
    }
  }

  async function handleStatusUpdate(event) {
    event.preventDefault();
    if (!selectedTicket) return;
    setMutationState({
      status: "saving",
      message: "",
      errorMessage: "",
    });
    try {
      const result = await updateSupportTicketStatus({
        ticket_id: selectedTicket.ticket_id,
        status: statusForm.status,
        message: statusForm.message,
        expected_version: selectedTicket.version,
      });
      setStatusForm((current) => ({ ...current, message: "" }));
      await refreshAfterMutation(result, "Support status updated.");
    } catch (error) {
      setMutationState({
        status: "error",
        message: "",
        errorMessage:
          error.message || "The support status could not be updated.",
      });
    }
  }

  async function handleRequesterReview(outcome) {
    if (!selectedTicket) return;
    setMutationState({
      status: "saving",
      message: "",
      errorMessage: "",
    });
    try {
      const result = await reviewSupportTicket({
        ticket_id: selectedTicket.ticket_id,
        outcome,
        message: reviewMessage,
        expected_version: selectedTicket.version,
      });
      setReviewMessage("");
      await refreshAfterMutation(
        result,
        outcome === "fixed"
          ? "The ticket is complete. Thank you for reviewing it."
          : "Marked Not Fixed. The ticket is back in the work queue.",
      );
    } catch (error) {
      setMutationState({
        status: "error",
        message: "",
        errorMessage: error.message || "The review could not be saved.",
      });
    }
  }

  function applyTicketSearch(event) {
    event.preventDefault();
    setAppliedSearch(searchInput.trim());
  }

  function selectTicketScope(nextScope) {
    if (nextScope !== "all") {
      setSelectedRequesterIds([]);
      setReportedEnvironment("");
    }
    setScope(nextScope);
  }

  function toggleRequesterFilter(userId) {
    setSelectedRequesterIds((current) =>
      current.includes(userId)
        ? current.filter((value) => value !== userId)
        : [...current, userId],
    );
  }

  function refreshTicketQueue() {
    setLoadingTicketId("");
    setDetailState({
      status: "idle",
      data: null,
      errorMessage: "",
    });
    setMutationState({
      status: "idle",
      message: "",
      errorMessage: "",
    });
    void loadTickets();
  }

  function selectCreateFiles(fileList) {
    const files = Array.from(fileList || []);
    setCreateForm((current) => ({
      ...current,
      files: mergeSelectedFiles(current.files, files),
    }));
    setCreateFileKey((value) => value + 1);
  }

  function removeCreateFile(file) {
    setCreateForm((current) => ({
      ...current,
      files: current.files.filter((item) => item !== file),
    }));
    setCreateFileKey((value) => value + 1);
  }

  function handleCreateFileDrop(event) {
    event.preventDefault();
    setCreateDropActive(false);
    selectCreateFiles(event.dataTransfer?.files);
  }

  function addCreateInlineScreenshots(files) {
    setCreateForm((current) => ({
      ...current,
      files: mergeSelectedFiles(current.files, files),
      inlineFileNames: [
        ...new Set([
          ...current.inlineFileNames,
          ...files.map((file) => file.name),
        ]),
      ],
    }));
  }

  function removeCreateInlineScreenshot(file) {
    setCreateForm((current) => ({
      ...current,
      files: current.files.filter((item) => item !== file),
      inlineFileNames: current.inlineFileNames.filter(
        (name) => name !== file.name,
      ),
    }));
  }

  function replaceCreateInlineScreenshot(previousFile, nextFile) {
    setCreateForm((current) => ({
      ...current,
      files: current.files.map((file) =>
        file === previousFile ? nextFile : file,
      ),
      inlineFileNames: current.inlineFileNames.map((name) =>
        name === previousFile.name ? nextFile.name : name,
      ),
    }));
  }

  function selectCommentFiles(fileList) {
    const files = Array.from(fileList || []);
    setCommentForm((current) => ({
      ...current,
      files: mergeSelectedFiles(current.files, files),
    }));
    setCommentFileKey((value) => value + 1);
  }

  function removeCommentFile(file) {
    setCommentForm((current) => ({
      ...current,
      files: current.files.filter((item) => item !== file),
    }));
    setCommentFileKey((value) => value + 1);
  }

  function addCommentInlineScreenshots(files) {
    setCommentForm((current) => ({
      ...current,
      files: mergeSelectedFiles(current.files, files),
      inlineFileNames: [
        ...new Set([
          ...current.inlineFileNames,
          ...files.map((file) => file.name),
        ]),
      ],
    }));
  }

  function removeCommentInlineScreenshot(file) {
    setCommentForm((current) => ({
      ...current,
      files: current.files.filter((item) => item !== file),
      inlineFileNames: current.inlineFileNames.filter(
        (name) => name !== file.name,
      ),
    }));
  }

  function replaceCommentInlineScreenshot(previousFile, nextFile) {
    setCommentForm((current) => ({
      ...current,
      files: current.files.map((file) =>
        file === previousFile ? nextFile : file,
      ),
      inlineFileNames: current.inlineFileNames.map((name) =>
        name === previousFile.name ? nextFile.name : name,
      ),
    }));
  }

  const createExternalFiles = createForm.files.filter(
    (file) => !createForm.inlineFileNames.includes(file.name),
  );
  const commentExternalFiles = commentForm.files.filter(
    (file) => !commentForm.inlineFileNames.includes(file.name),
  );

  if (profileState.status === "loading") {
    return (
      <div className="support-page-shell">
        <div className="support-page">
          <section className="support-card card shadow-sm">
            <SupportSectionLoading label="Loading support access…" />
          </section>
        </div>
      </div>
    );
  }

  if (profileState.status === "error") {
    return (
      <div className="support-page-shell">
        <div className="support-page">
          <section className="support-card card shadow-sm">
            <h1>Support could not be loaded</h1>
            <p className="support-error-note alert alert-danger">
              {profileState.errorMessage}
            </p>
          </section>
        </div>
      </div>
    );
  }

  if (!profile?.has_access) {
    return (
      <div className="support-page-shell">
        <div className="support-page">
          <section className="support-card card shadow-sm">
            <h1>Support is unavailable</h1>
            <p>
              This account is not included in the approved Support access
              group.
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="support-page-shell">
      <div className="support-page">
        {profileState.errorMessage ? (
          <p className="support-error-note alert alert-danger">
            {profileState.errorMessage}
          </p>
        ) : null}
        {mutationState.message ? (
          <p className="support-status-note alert alert-success">
            {mutationState.message}
          </p>
        ) : null}
        {mutationState.errorMessage ? (
          <p className="support-error-note alert alert-danger">
            {mutationState.errorMessage}
          </p>
        ) : null}

        <section className="support-card support-create-card card shadow-sm">
          <div className="support-section-heading">
            <div>
              <h1>Create New Ticket</h1>
            </div>
          </div>

          <form
            className="support-form"
            onSubmit={handleCreate}
            onKeyDown={preventImplicitTicketSubmit}
          >
            <label className="support-field-shell">
              <span>Subject</span>
              <input
                className="support-text-input form-control"
                type="text"
                maxLength={180}
                value={createForm.subject}
                onChange={(event) => {
                  const subject = event.target.value;
                  setCreateForm((current) => ({
                    ...current,
                    subject,
                  }));
                }}
                required
              />
            </label>

            <div className="support-field-shell">
              <label htmlFor="support-request-description">Description</label>
              <div
                className={`support-description-dropzone${
                  createDropActive ? " is-dragging" : ""
                }`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setCreateDropActive(true);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "copy";
                  setCreateDropActive(true);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setCreateDropActive(false);
                  }
                }}
                onDrop={handleCreateFileDrop}
              >
                <SupportNarrativeEditor
                  key={createEditorKey}
                  id="support-request-description"
                  label="Description"
                  value={createForm.description}
                  onChange={(description) =>
                    setCreateForm((current) => ({
                      ...current,
                      description,
                    }))
                  }
                  onAddScreenshots={addCreateInlineScreenshots}
                  onRemoveScreenshot={removeCreateInlineScreenshot}
                  onReplaceScreenshot={replaceCreateInlineScreenshot}
                />
                <StagedFileList
                  files={createExternalFiles}
                  destination="ticket"
                  onRemove={removeCreateFile}
                />
                <div className="support-description-upload">
                  <div>
                    <strong>
                      {createDropActive
                        ? "Drop files to attach"
                        : "Drag and drop files here"}
                    </strong>
                    <small>
                      Up to {profile?.max_files_per_action || 5} files,{" "}
                      {Math.round(
                        (profile?.max_total_file_bytes || 6 * 1024 * 1024) /
                          (1024 * 1024),
                      )}{" "}
                      MB total per ticket or comment.
                    </small>
                  </div>
                  <label className="support-button support-button-secondary support-file-browse btn btn-outline-secondary">
                    Browse Files
                    <input
                      key={createFileKey}
                      type="file"
                      accept={acceptValue}
                      multiple
                      aria-label="Browse files to attach"
                      onChange={(event) =>
                        selectCreateFiles(event.target.files)
                      }
                    />
                  </label>
                </div>
              </div>
              <small className="support-muted">
                Do not include passwords, authentication codes, tax IDs, or full
                account numbers.
              </small>
            </div>

            <div className="support-form-actions">
              <button
                className="support-button support-button-primary btn btn-primary"
                type="submit"
                disabled={mutationState.status === "saving"}
              >
                {mutationState.status === "saving"
                  ? "Submitting"
                  : "Submit Request"}
              </button>
              <button
                className="support-button support-button-secondary btn btn-outline-secondary"
                type="button"
                disabled={mutationState.status === "saving"}
                onClick={cancelCreateDraft}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>

        <div className="support-layout">
          <section
            className="support-card support-list-card card shadow-sm"
            aria-busy={ticketsState.status === "loading"}
          >
            <div className="support-section-heading">
              <div>
                <p className="support-card-kicker">
                  {canViewShared && scope === "all"
                    ? profile?.can_manage
                      ? "Work Queue"
                      : "Shared Queue"
                    : "My Tickets"}
                </p>
                <h2>Support Tickets</h2>
              </div>
              <div className="support-queue-heading-actions">
                <button
                  className="support-button support-button-secondary btn btn-outline-secondary"
                  type="button"
                  disabled={ticketsState.status === "loading"}
                  onClick={refreshTicketQueue}
                >
                  Refresh
                </button>
                <span
                  className="support-count"
                  aria-label={`${ticketsState.totalCount} ticket${
                    ticketsState.totalCount === 1 ? "" : "s"
                  }`}
                  aria-live="polite"
                >
                  {ticketsState.totalCount}
                </span>
              </div>
            </div>

            {canViewShared ? (
              <div className="support-scope-row">
                <button
                  className={`support-segment btn${
                    scope === "mine" ? " is-active" : ""
                  }`}
                  type="button"
                  onClick={() => selectTicketScope("mine")}
                >
                  My Tickets
                </button>
                <button
                  className={`support-segment btn${
                    scope === "all" ? " is-active" : ""
                  }`}
                  type="button"
                  onClick={() => selectTicketScope("all")}
                >
                  {profile?.can_manage ? "All Tickets" : "Shared Tickets"}
                </button>
              </div>
            ) : null}

            <form className="support-filter-row" onSubmit={applyTicketSearch}>
              <input
                className="support-text-input form-control"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search tickets"
                aria-label="Search tickets"
              />
              <button
                className="support-button support-button-secondary btn btn-outline-secondary"
                type="submit"
              >
                Search
              </button>
            </form>

            <label className="support-checkbox">
              <input
                type="checkbox"
                checked={includeCompleted}
                onChange={(event) => setIncludeCompleted(event.target.checked)}
              />
              <span>Include completed and closed tickets</span>
            </label>

            {canViewShared && scope === "all" ? (
              <>
                <label className="support-environment-filter">
                  <span>Filter by environment</span>
                  <select
                    className="form-control"
                    value={reportedEnvironment}
                    onChange={(event) =>
                      setReportedEnvironment(event.target.value)
                    }
                  >
                    <option value="">All environments</option>
                    {(profile?.reported_environments ||
                      Object.entries(SUPPORT_ENVIRONMENT_LABELS).map(
                        ([value, label]) => ({ value, label }),
                      )).map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <details className="support-person-filter">
                  <summary>
                    <span className="support-person-filter-heading">
                      <span>Filter by person</span>
                      <small>
                        {selectedRequesterIds.length
                          ? `${selectedRequesterIds.length} selected`
                          : "All people"}
                      </small>
                    </span>
                  </summary>
                  <div
                    className="support-person-filter-body"
                    role="group"
                    aria-label="People to include"
                  >
                    {selectedRequesterIds.length ? (
                      <button
                        className="support-clear-filter"
                        type="button"
                        onClick={() => setSelectedRequesterIds([])}
                      >
                        Clear person filter
                      </button>
                    ) : null}
                    <div className="support-person-list">
                      {requesterOptions.map((requester) => {
                        const userId = String(requester.user_id || "");
                        const displayName =
                          requester.display_name || requester.login || userId;
                        return (
                          <label className="support-person-option" key={userId}>
                            <input
                              type="checkbox"
                              checked={selectedRequesterIds.includes(userId)}
                              onChange={() => toggleRequesterFilter(userId)}
                            />
                            <span>
                              <strong>{displayName}</strong>
                              <small>
                                {[
                                  requester.login !== displayName
                                    ? requester.login
                                    : "",
                                  requester.company_alias,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </small>
                            </span>
                          </label>
                        );
                      })}
                      {!requesterOptions.length ? (
                        <span className="support-muted">
                          No ticket creators are available.
                        </span>
                      ) : null}
                    </div>
                  </div>
                </details>

                <div
                  className="support-category-summary"
                  aria-label="Ticket counts by status"
                >
                  <p className="support-card-kicker">Tickets by Status</p>
                  <ul className="support-category-counts">
                    {statusSummary.map(({ status, label, count }) => (
                      <li
                        className={`support-category-count ${ticketCardStatusClass(
                          status,
                        )}`}
                        key={status}
                        aria-label={`${label}: ${count} ticket${
                          count === 1 ? "" : "s"
                        }`}
                      >
                        <span>{label}</span>
                        <strong>{count}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : null}

            {ticketsState.status === "loading" ? (
              <SupportSectionLoading
                className="support-list-loading"
                label="Loading support tickets…"
              />
            ) : null}
            {ticketsState.errorMessage ? (
              <p className="support-error-note alert alert-danger">
                {ticketsState.errorMessage}
              </p>
            ) : null}

            <div className="support-ticket-list">
              {ticketList.map((ticket) => (
                <button
                  key={ticket.ticket_id}
                  className={`support-ticket-button ${ticketCardStatusClass(
                    ticket.status,
                  )}${activeTicketId === ticket.ticket_id ? " is-active" : ""}`}
                  type="button"
                  onClick={() => void openTicket(ticket.ticket_id)}
                >
                  <span className="support-ticket-top">
                    <span className="support-ticket-identifiers">
                      <strong>{ticket.ticket_number}</strong>
                      <span
                        className={`support-environment-badge support-environment-${
                          ticket.reported_environment || "legacy"
                        }`}
                      >
                        {ticket.reported_environment_label ||
                          SUPPORT_ENVIRONMENT_LABELS.legacy}
                      </span>
                    </span>
                    <span className={statusClass(ticket.status)}>
                      {ticket.status_label}
                    </span>
                  </span>
                  <span className="support-ticket-subject">
                    {ticket.subject}
                  </span>
                  <span className="support-ticket-requester">
                    <span>Submitted by</span>
                    <strong>
                      {ticket.requester_display_name || ticket.requester_login}
                    </strong>
                  </span>
                  <span className="support-ticket-preview">
                    {supportNarrativePreview(ticket.description_preview)}
                  </span>
                  <span className="support-ticket-meta">
                    {profile?.can_manage && scope === "all"
                      ? `${ticket.company_alias} · `
                      : ""}
                    {formatTimestamp(ticket.last_activity_at)}
                    {ticket.attachment_count
                      ? ` · ${ticket.attachment_count} file${
                          ticket.attachment_count === 1 ? "" : "s"
                        }`
                      : ""}
                  </span>
                </button>
              ))}
              {ticketsState.status === "ready" && !ticketList.length ? (
                <div className="support-empty">
                  <strong>No support tickets found.</strong>
                  <span>Submit a new ticket above whenever you need help.</span>
                </div>
              ) : null}
            </div>
          </section>

          <section
            className="support-card support-detail-card card shadow-sm"
            aria-busy={detailState.status === "loading"}
          >
            {detailState.status === "idle" ? (
              <div className="support-empty support-empty-detail">
                <strong>Select a support ticket</strong>
                <span>
                  Its description, files, status, and full audit trail will
                  appear here.
                </span>
              </div>
            ) : null}
            {detailState.status === "loading" && !selectedTicket ? (
              <SupportSectionLoading
                className="support-empty-detail"
                label="Loading ticket details…"
              />
            ) : null}
            {detailState.status === "loading" && selectedTicket ? (
              <SupportSectionLoading
                className="support-detail-loading"
                label="Loading ticket details…"
              />
            ) : null}
            {detailState.errorMessage ? (
              <p className="support-error-note alert alert-danger">
                {detailState.errorMessage}
              </p>
            ) : null}

            {selectedTicket ? (
              <div className="support-detail">
                <div className="support-detail-heading">
                  <div>
                    <p className="support-card-kicker">
                      {selectedTicket.ticket_number}
                    </p>
                    <h2>{selectedTicket.subject}</h2>
                  </div>
                  <span className={statusClass(selectedTicket.status)}>
                    {selectedTicket.status_label}
                  </span>
                </div>

                <dl className="support-detail-meta">
                  <div>
                    <dt>Submitted By</dt>
                    <dd>{selectedTicket.requester_display_name}</dd>
                  </div>
                  <div>
                    <dt>Company</dt>
                    <dd>{selectedTicket.company_alias}</dd>
                  </div>
                  <div>
                    <dt>Environment</dt>
                    <dd>
                      <span
                        className={`support-environment-badge support-environment-${
                          selectedTicket.reported_environment || "legacy"
                        }`}
                      >
                        {selectedTicket.reported_environment_label ||
                          SUPPORT_ENVIRONMENT_LABELS.legacy}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Submitted</dt>
                    <dd>{formatTimestamp(selectedTicket.created_at)}</dd>
                  </div>
                  <div>
                    <dt>Last Activity</dt>
                    <dd>{formatTimestamp(selectedTicket.last_activity_at)}</dd>
                  </div>
                </dl>

                <div className="support-description">
                  <span>Description</span>
                  <SupportNarrative
                    content={selectedTicket.description}
                    attachments={attachments}
                  />
                </div>

                <div className="support-subsection">
                  <div className="support-subsection-heading">
                    <h3>Attachments</h3>
                    <span>{attachments.length}</span>
                  </div>
                  <div className="support-attachment-list">
                    {attachments.map((attachment) => (
                      <a
                        key={attachment.attachment_id}
                        className="support-attachment"
                        href={supportAttachmentUrl(attachment.attachment_id)}
                      >
                        <span>
                          <strong>{attachment.file_name}</strong>
                          <small>
                            {formatBytes(attachment.size_bytes)} ·{" "}
                            {formatTimestamp(attachment.uploaded_at)}
                          </small>
                        </span>
                        <span aria-hidden="true">Download</span>
                      </a>
                    ))}
                    {!attachments.length ? (
                      <p className="support-muted">
                        No files have been attached.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="support-subsection">
                  <div className="support-subsection-heading">
                    <h3>Activity &amp; Audit Trail</h3>
                    <span>{activity.length}</span>
                  </div>
                  <ol className="support-activity-list">
                    {activity.map((item) => (
                      <li
                        key={item.activity_id}
                        className="support-activity-item"
                      >
                        <div className="support-activity-top">
                          <strong>{activityHeading(item)}</strong>
                          <time>{formatTimestamp(item.created_at)}</time>
                        </div>
                        <span className="support-activity-actor">
                          {item.actor_display_name} ·{" "}
                          {activityActorLabel(item, selectedTicket)}
                        </span>
                        {profile?.can_manage && activityTechnicalNote(item) ? (
                          <span className="support-activity-technical">
                            {activityTechnicalNote(item)}
                          </span>
                        ) : null}
                        {ticketStatusTransition(item) ? (
                          <span className="support-transition">
                            {ticketStatusTransition(item)}
                          </span>
                        ) : null}
                        {item.body ? (
                          <SupportNarrative
                            content={item.body}
                            attachments={attachments}
                          />
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>

                {selectedTicket.requester_can_review ? (
                  <div className="support-action-panel support-review-panel">
                    <div>
                      <p className="support-card-kicker">
                        Your Review Required
                      </p>
                      <h3>Did the fix work correctly?</h3>
                      <p>
                        Choose one of the actions below to submit your decision.
                        Typing in Review Note does not send it by itself.
                      </p>
                    </div>
                    <label className="support-field-shell">
                      <span>Review Note (Required for Not Fixed)</span>
                      <textarea
                        className="support-text-input support-textarea support-textarea-compact form-control"
                        value={reviewMessage}
                        onChange={(event) =>
                          setReviewMessage(event.target.value)
                        }
                        placeholder="Explain what is still incorrect"
                        aria-describedby="support-review-submit-guidance"
                      />
                      <small
                        className="support-muted"
                        id="support-review-submit-guidance"
                      >
                        Your note is sent only when you select a review action
                        below.
                      </small>
                    </label>
                    <div className="support-form-actions">
                      <button
                        className="support-button support-button-primary btn btn-primary"
                        type="button"
                        disabled={mutationState.status === "saving"}
                        onClick={() => void handleRequesterReview("fixed")}
                      >
                        Confirm Fixed &amp; Complete Ticket
                      </button>
                      <button
                        className="support-button support-button-secondary btn btn-outline-secondary"
                        type="button"
                        disabled={
                          mutationState.status === "saving" ||
                          !reviewMessage.trim()
                        }
                        onClick={() => void handleRequesterReview("not_fixed")}
                      >
                        Submit Feedback as Not Fixed
                      </button>
                    </div>
                  </div>
                ) : null}

                {selectedTicket.can_manage ? (
                  <form
                    className="support-action-panel"
                    onSubmit={handleStatusUpdate}
                  >
                    <div>
                      <p className="support-card-kicker">Support Workflow</p>
                      <h3>Update this ticket</h3>
                      <p>
                        Schedule finished work for release, then request user
                        review after that release is live. Only the requester
                        can complete a ticket.
                      </p>
                    </div>
                    <label className="support-field-shell">
                      <span>Next Status</span>
                      <select
                        className="support-text-input form-control"
                        value={statusForm.status}
                        onChange={(event) => {
                          const status = event.target.value;
                          setStatusForm((current) => ({
                            ...current,
                            status,
                          }));
                        }}
                        required
                      >
                        {availableStatusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="support-field-shell">
                      <span>
                        Status Note{" "}
                        {statusNoteRequired ? "(Required)" : "(Optional)"}
                      </span>
                      <textarea
                        className="support-text-input support-textarea support-textarea-compact form-control"
                        value={statusForm.message}
                        onChange={(event) => {
                          const message = event.target.value;
                          setStatusForm((current) => ({
                            ...current,
                            message,
                          }));
                        }}
                        placeholder={
                          statusNoteRequired
                            ? "Describe what was fixed before requesting user review."
                            : "Add a note only if more context would help."
                        }
                        required={statusNoteRequired}
                      />
                    </label>
                    <div className="support-form-actions">
                      <button
                        className="support-button support-button-primary btn btn-primary"
                        type="submit"
                        disabled={
                          mutationState.status === "saving" ||
                          !availableStatusOptions.length
                        }
                      >
                        Update Status
                      </button>
                    </div>
                  </form>
                ) : null}

                {selectedTicket.can_comment ? (
                  <form
                    className="support-action-panel"
                    onSubmit={handleComment}
                  >
                    <div>
                      <p className="support-card-kicker">Send a Message</p>
                      <h3>Comment without changing the ticket status</h3>
                      <p>
                        Use this section for a question or reply that should not
                        mark the ticket Fixed or Not Fixed.
                      </p>
                    </div>
                    <div className="support-field-shell">
                      <label htmlFor="support-comment-editor">Comment</label>
                      <SupportNarrativeEditor
                        key={commentEditorKey}
                        id="support-comment-editor"
                        label="Comment"
                        compact
                        value={commentForm.message}
                        onChange={(message) =>
                          setCommentForm((current) => ({
                            ...current,
                            message,
                          }))
                        }
                        onAddScreenshots={addCommentInlineScreenshots}
                        onRemoveScreenshot={removeCommentInlineScreenshot}
                        onReplaceScreenshot={replaceCommentInlineScreenshot}
                      />
                      <p className="support-muted">
                        Typing in Comment does not send it. Select Send Comment
                        below.
                      </p>
                    </div>
                    <StagedFileList
                      files={commentExternalFiles}
                      destination="comment"
                      onRemove={removeCommentFile}
                    />
                    <label className="support-field-shell">
                      <span>Additional Files</span>
                      <input
                        key={commentFileKey}
                        className="support-text-input support-file-input form-control"
                        type="file"
                        accept={acceptValue}
                        multiple
                        aria-label="Browse files for comment"
                        onChange={(event) =>
                          selectCommentFiles(event.target.files)
                        }
                      />
                      <small className="support-file-summary">
                        Paste a screenshot into the Comment box or browse for
                        files.
                      </small>
                    </label>
                    <div className="support-form-actions">
                      <button
                        className="support-button support-button-primary btn btn-primary"
                        type="submit"
                        disabled={mutationState.status === "saving"}
                      >
                        Send Comment
                      </button>
                    </div>
                  </form>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
